import { ISankeyData, SankeyChart } from 'app/components/charts/SankeyChart';
import { useStore } from 'app/context';
import { AgtAkumaPrimeKwVM, AkumaPrimeType } from 'app/stores/ui/agt.akumaprime.ui.store';

import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import colors from 'assets/scss/colors.module.scss';
import _ from 'lodash';
import { countArray } from 'app/utils';

interface IAkumaPrimeSankeyData extends ISankeyData {
	einordnung?: string[];
	beziehung?: string[];
	terminierung?: string[];
}

const getSchema = (type: AkumaPrimeType) => {
	if (type === AkumaPrimeType.terminierung) return terminierungsSankeySchema;
	return einordnungsSankeySchema;
};

const einordnungsSankeySchema: IAkumaPrimeSankeyData[] = [
	{ source: 'Eingeordnet', target: 'Kranken', targetColor: colors.green, einordnung: ['K'] },
	{ source: 'Eingeordnet', target: 'Leben', targetColor: colors.blue, einordnung: ['L'] },
	{ source: 'Eingeordnet', target: 'Sach', targetColor: colors.yellow, einordnung: ['S'] },
	{ source: 'Eingeordnet', target: 'Sonstige', targetColor: colors.orange, einordnung: ['SO'] },
	{ source: 'Eingeordnet', target: 'Fonds', targetColor: colors.richTurquoise, einordnung: ['F'] },
	{ source: 'Eingeordnet', target: 'Bestehende Aktion', targetColor: colors.purple, einordnung: ['BA'] },
	{ source: 'Eingeordnet', target: 'Nicht kontaktieren', targetColor: colors.richRed, einordnung: ['NK'] },
	{ source: 'Einzuordnende Kunden', target: 'Eingeordnet', sourceColor: colors.blue, targetColor: colors.richGreen, einordnung: ['K', 'L', 'S', 'SO', 'F', 'BA', 'NK'] },
	{ source: 'Einzuordnende Kunden', target: 'Nicht eingeordnet', sourceColor: colors.turquoise, targetColor: colors.greyLight, einordnung: ['NE'] },
];

const terminierungsSankeySchema: IAkumaPrimeSankeyData[] = [
	{ source: 'Terminierung', target: 'Terminierung durch Agentur', targetColor: colors.darkYellow, terminierung: ['AGT'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Terminierung', target: 'Externe Terminierung', targetColor: colors.richTurquoise, terminierung: ['CC'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Terminierung durch Agentur', target: 'Beziehung unbekannt', targetColor: colors.darkYellow, terminierung: ['AGT'], beziehung: ['BZU'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Terminierung durch Agentur', target: 'Beziehung weniger gut', targetColor: colors.richRed, terminierung: ['AGT'], beziehung: ['BZWG'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Terminierung durch Agentur', target: 'Beziehung mittel', targetColor: colors.richBlue, terminierung: ['AGT'], beziehung: ['BZM'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Terminierung durch Agentur', target: 'Beziehung gut', targetColor: colors.richGreen, terminierung: ['AGT'], beziehung: ['BZG'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Externe Terminierung', target: 'Beziehung unbekannt ', targetColor: colors.darkYellow, terminierung: ['CC'], beziehung: ['BZU'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Externe Terminierung', target: 'Beziehung weniger gut ', targetColor: colors.richRed, terminierung: ['CC'], beziehung: ['BZWG'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Externe Terminierung', target: 'Beziehung mittel ', targetColor: colors.richBlue, terminierung: ['CC'], beziehung: ['BZM'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
	{ source: 'Externe Terminierung', target: 'Beziehung gut ', targetColor: colors.richGreen, terminierung: ['CC'], beziehung: ['BZG'], einordnung: ['K', 'L', 'S', 'SO', 'F'] },
];

function getChartData(schema: IAkumaPrimeSankeyData[], data: AgtAkumaPrimeKwVM[], type: AkumaPrimeType): ISankeyData[] {
	const ret: ISankeyData[] = [];

	// Map the schema nodes to the appropriate aggregated values based on type
	schema.forEach((node) => {
		let val = 0;

		switch (type) {
			case AkumaPrimeType.einordnung:
				// Handle einordnung chart nodes
				if (node.source === 'Einzuordnende Kunden' && node.target === 'Eingeordnet') {
					// Sum of all categorized customers
					val = data.reduce((sum, d) => sum + d.eingeordnetTotal, 0);
				} else if (node.source === 'Einzuordnende Kunden' && node.target === 'Nicht eingeordnet') {
					// Customers not categorized
					val = data.reduce((sum, d) => sum + d.einordnungNichtEingeordnet, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Kranken') {
					val = data.reduce((sum, d) => sum + d.einordnungKranken, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Leben') {
					val = data.reduce((sum, d) => sum + d.einordnungLeben, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Sach') {
					val = data.reduce((sum, d) => sum + d.einordnungSach, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Sonstige') {
					val = data.reduce((sum, d) => sum + d.einordnungSonstige, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Fonds') {
					val = data.reduce((sum, d) => sum + d.einordnungFonds, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Nicht kontaktieren') {
					val = data.reduce((sum, d) => sum + d.einordnungNichtKontaktieren, 0);
				} else if (node.source === 'Eingeordnet' && node.target === 'Bestehende Aktion') {
					val = data.reduce((sum, d) => sum + d.einordnungBestehendeAktion, 0);
				}
				break;

			case AkumaPrimeType.terminierung:
				// Handle terminierung chart nodes
				if (node.source === 'Terminierung' && node.target === 'Terminierung durch Agentur') {
					val = data.reduce((sum, d) => sum + d.terminierungAgentur, 0);
				} else if (node.source === 'Terminierung' && node.target === 'Externe Terminierung') {
					val = data.reduce((sum, d) => sum + d.terminierungExternTotal, 0);
				} else if (node.source === 'Terminierung durch Agentur') {
					// Handle relationship splits from agency appointments
					if (node.target && node.target.includes('Beziehung unbekannt')) {
						// For agency appointments, split beziehung values proportionally
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungUnbekannt / totalBeziehungen;
							return sum + Math.round(d.terminierungAgentur * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung weniger gut')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungWenigerGut / totalBeziehungen;
							return sum + Math.round(d.terminierungAgentur * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung mittel')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungMittel / totalBeziehungen;
							return sum + Math.round(d.terminierungAgentur * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung gut')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungGut / totalBeziehungen;
							return sum + Math.round(d.terminierungAgentur * proportion * 100) / 100;
						}, 0);
					}
				} else if (node.source === 'Externe Terminierung') {
					// Handle relationship splits from external appointments
					if (node.target && node.target.includes('Beziehung unbekannt')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungUnbekannt / totalBeziehungen;
							return sum + Math.round(d.terminierungExternTotal * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung weniger gut')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungWenigerGut / totalBeziehungen;
							return sum + Math.round(d.terminierungExternTotal * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung mittel')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungMittel / totalBeziehungen;
							return sum + Math.round(d.terminierungExternTotal * proportion * 100) / 100;
						}, 0);
					} else if (node.target && node.target.includes('Beziehung gut')) {
						val = data.reduce((sum, d) => {
							const totalBeziehungen = d.beziehungUnbekannt + d.beziehungWenigerGut + d.beziehungMittel + d.beziehungGut;
							if (totalBeziehungen === 0) return sum;
							const proportion = d.beziehungGut / totalBeziehungen;
							return sum + Math.round(d.terminierungExternTotal * proportion * 100) / 100;
						}, 0);
					}
				}
				break;

			default:
				break;
		}

		ret.push({ ...node, value: val });
	});

	return ret;
}

interface IAkumaPrimeSankeyChartType {
	type: AkumaPrimeType;
	data: AgtAkumaPrimeKwVM[];
}
export const AkumaPrimeSankeyChart = observer((props: IAkumaPrimeSankeyChartType) => {
	const { agtAkumaPrimeUiStore, berichteUiiStore, agenturListUiStore } = useStore();

	const [chartData, setChartData] = useState<ISankeyData[]>();
	const [itemsTotal, setItemsTotal] = useState<number>(0);

	useEffect(() => {
		let kwdata = props.data;
		if (!berichteUiiStore.selectedAgt) {
			kwdata = agenturListUiStore.applyFilterOnAgt(kwdata) as AgtAkumaPrimeKwVM[];
		}

		const schema = getSchema(props.type);
		const cdata = getChartData(schema, kwdata, props.type);
		setChartData(cdata);

		const einordnungTotal = countArray(kwdata.map((e) => e.einordnungTotal));
		// For terminierung, the total should be the sum of agency and external scheduling
		const terminierungTotal = countArray(kwdata.map((e) => e.terminierungAgentur + e.terminierungExternTotal));
		setItemsTotal(props.type === AkumaPrimeType.terminierung ? terminierungTotal : einordnungTotal);
	}, [agenturListUiStore, agenturListUiStore.filterHasChanged, agtAkumaPrimeUiStore, agtAkumaPrimeUiStore.currentKWItems, berichteUiiStore.selectedAgt, props.type, props.data]);

	const labelFormat = (params: any) => {
		const perc: string = itemsTotal > 0 ? `{italic|(${Math.round((params.value / itemsTotal) * 100)}%)}` : '';
		const val = Math.round(params.value * 1) / 1;
		return `{bold|${params.name}} ${val} ${perc}`;
	};

	return <>{chartData && <SankeyChart data={chartData} labelFormatter={labelFormat} />}</>;
});
